import { Typography, Box, Card, Tooltip, IconButton } from "@mui/material";
import DataTable from "examples/Tables/DataTable";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import DeleteIcon from "@mui/icons-material/Delete";
import { useEffect, useState } from "react";
import { getRegularUsers, deleteRegularUser } from "services/usersServices";
import CircularProgress from "@mui/material/CircularProgress";
import { toast } from "react-toastify";
import SearchBar from "../../Components/SearchBar";
import { useDebouncedSearch } from "../../../../hooks/useDebouncedSearch";
import AvatarWithLoader from "../../Components/Avatar/AvatarWithLoader";
import ActionModal from "components/Modals/ActionModal";

export default function RegularUsers() {
  const [users, setUsers] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [loading, setLoading] = useState(true);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const { searchTerm, debouncedSearchTerm, handleSearchChange } =
    useDebouncedSearch(500);

  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      console.log("Search submitted:", searchTerm);
    }
  };

  const formatLastActivity = (lastActivity) => {
    if (!lastActivity) {
      return "NA";
    }
    const date = new Date(lastActivity);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const tableData = {
    columns: [
      { Header: "Name", accessor: "name", align: "left" },
      { Header: "Bio", accessor: "bio", align: "left" },
      { Header: "Last Activity", accessor: "lastActivity", align: "left" },
      { Header: "Action", accessor: "action", align: "left" },
    ],
    rows:
      users?.map((user) => ({
        name: (
          <Box display="flex" alignItems="center">
            <AvatarWithLoader src={user?.avatar} alt={user?.name} />
            <Typography variant="h6" fontWeight="bold">
              {user?.name}
            </Typography>
          </Box>
        ),
        bio: (
          <Tooltip title={user?.bio}>
            <Typography variant="h6" color="text.secondary">
              {user?.bio.length > 80
                ? user?.bio.slice(0, 80) + "..."
                : user?.bio}
            </Typography>
          </Tooltip>
        ),
        lastActivity: (
          <Typography variant="h6" fontWeight="bold">
            {formatLastActivity(user?.lastActivity)}
          </Typography>
        ),
        action: (
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDeleteUser(user._id)}
          >
            <DeleteIcon fontSize="small" />
            <Typography fontSize={12} fontWeight={700}>
              Delete
            </Typography>
          </IconButton>
        ),
      })) || [],
  };

  useEffect(() => {
    setLoading(true);
    getRegularUsers(debouncedSearchTerm || null)
      .then((res) => {
        setUsers(res.data);
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        toast.error("Failed to fetch regular users");
        setLoading(false);
      });
  }, [debouncedSearchTerm, refresh]);

  const handleDeleteUser = (id) => {
    setSelectedUser(id);
    setOpenDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    deleteRegularUser(selectedUser)
      .then((res) => {
        toast.success("User deleted successfully");
        setOpenDeleteModal(false);
        setSelectedUser(null);
        setRefresh(!refresh);
      })
      .catch((err) => {
        toast.error("Failed to delete user");
        setOpenDeleteModal(false);
        setSelectedUser(null);
      });
  };

  return (
    <ArgonBox>
      <SearchBar
        value={searchTerm}
        onChange={handleSearchChange}
        onKeyDown={handleSearchKeyDown}
        placeholder="Search regular users"
      />

      <Card sx={{ overflow: "visible", mb: 10, mt: 5, padding: 3 }}>
        <ArgonBox p={3}>
          <ArgonTypography variant="h5" fontWeight="medium">
            Regular Users
          </ArgonTypography>
        </ArgonBox>
        <ArgonBox>
          {loading ? (
            <ArgonBox display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </ArgonBox>
          ) : users.length > 0 ? (
            <DataTable
              table={{ columns: tableData.columns, rows: tableData.rows }}
              entriesPerPage={false}
              canSearch={false}
              showTotalEntries={false}
              pagination={false}
            />
          ) : (
            <ArgonBox display="flex" justifyContent="center" p={3}>
              <ArgonTypography variant="h6">No users found</ArgonTypography>
            </ArgonBox>
          )}
        </ArgonBox>
      </Card>
      {openDeleteModal && (
        <ActionModal
          open={openDeleteModal}
          handleClose={() => setOpenDeleteModal(false)}
          handleYes={handleConfirmDelete}
          handleNo={() => setOpenDeleteModal(false)}
          color="#e53e3e"
          title="Delete User"
          detail="Are you sure you want to delete this user?"
        />
      )}
    </ArgonBox>
  );
}
